import { Text, Container, Flex, Heading, HStack, Stack, Box, Button, Tr, Td, Th, useToast, useDisclosure, Grid, Thead, Tbody, Table, Checkbox } from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Form, IconButton } from "components";
import Select2 from "./select2";
import Confirmation from "./confirmation";
import { API, Mask } from "config";

export default function Sales(){
  const toast = useToast()
  const confirmation = useDisclosure()
  const quantityRef = useRef(null)

  const shippingTypes = [
    {value:"1", label:"Para Entrega"},
    {value:"2", label:"Retirada no Núcleo"}
  ]

  const [distributor, setDistributor] = useState(null)
  const [productSelect, setProductSelect] = useState(null)
  const [product, setProduct] = useState(null)
  const [orderProducts, setOrderProducts] = useState([])
  const [shippingType, setShippingType] = useState("1")
  const [acceptZeroValue, setAcceptZeroValue] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState()

  const [form, setForm] = useState({
    quantity: 0,
    discount: 0,
    payment_methods: {
      balance: Mask.price(0),
      cielo: Mask.price(0),
    }
  })

  /* ================= LOADERS ================= */

  const loadDistributors = async (inputValue) => {
    const distributors = await API.get(`/distributors/list?page=1&term=${inputValue}`);
    const data = await distributors?.data

    return data?.data?.map((client) => ({
      value: client.id,
      label: client?.name ?? 'Sem nome',
      data: client,
    }))
  }

  const loadProducts = async (inputValue) => {
    const products = await API.get(`/products?term=${inputValue}`);
    const data = await products?.data

    return data?.map((product) => ({
      value:product.id,
      label: product.name,
      data: product
    }))
  }

  /* ================= CÁLCULOS ================= */

  const priceWithDiscount = useMemo(() => {
    const price = Number(product?.consultant_price ?? 0) / 100
    const discountPercentage = (form?.discount ?? 0) / 100
    return price - (price * discountPercentage)
  }, [product, form?.discount])

  const productTotal = useMemo(() => {
    return priceWithDiscount * (form?.quantity ?? 0)
  }, [priceWithDiscount, form?.quantity])

  const total = useMemo(() => {
    return orderProducts.reduce(
        (acc, item) => acc + Number(item?.total ?? 0),
        0
    );
  }, [orderProducts]);

  /* ================= EFFECT ================= */

  useEffect(() => {
    if (acceptZeroValue) {
      setForm((prev) => ({
        ...prev,
        payment_methods: {
          balance: Mask.price(0),
          cielo: Mask.price(0),
        }
      }));
    } else {
      const balance = Number(
          form?.payment_methods?.balance
              ?.replace('.', '')
              ?.replace(',','.') ?? 0
      )

      setForm((prev) => ({
        ...prev,
        payment_methods: {
          ...prev.payment_methods,
          cielo: Mask.price((total - balance).toFixed(2))
        }
      }))
    }
  }, [total, form?.payment_methods?.balance, acceptZeroValue])

  /* ================= HANDLERS ================= */

  const handleChange = ({target}) => {
    const { name, value } = target

    if (name === "balance_payment") {
      setForm((prev) => ({
        ...prev,
        payment_methods: {
          ...prev.payment_methods,
          balance: Mask.price(value),
        },
      }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  }

  const handleChooseProduct = (e) => {
    setProduct(e?.data)
    setProductSelect(e)
    setForm((prev) => ({
      ...prev,
      discount: e?.data?.min_discount ?? 0
    }))

    setTimeout(() => {
      quantityRef.current?.focus();
    }, 100);
  }

  const handleAddProduct = () => {
    let errors = {};

    if (!product) {
      toast({title:'Selecione um produto.', status:'warning'})
      return;
    }

    if (!form?.quantity || form?.quantity === 0) {
      errors.quantity = "A quantidade é obrigatória";
    }

    if (!form?.discount || form?.discount === 0) {
      errors.discount = "O desconto é obrigatório";
    }

    if (orderProducts.some((p) => p.id === product.id)) {
      toast({
        title: 'Este produto já foi adicionado. Remova-o para adicionar novamente.',
        status: 'warning'
      })
      return;
    }

    setError(errors)
    if (Object.keys(errors).length) return;

    setOrderProducts((prev) => [
      ...prev,
      {
        id: product.id,
        name: product.name,
        quantity: form.quantity,
        discount_percentage: form.discount,
        total: productTotal
      }
    ])

    setProduct(null)
    setProductSelect(null)
    setForm((prev) => ({
      ...prev,
      quantity: 0,
      discount: 0
    }))
  }

  const handleDelete = (id) => {
    setOrderProducts((prev) => prev.filter((p) => p.id !== id))
  }

  const handleClear = () => {
    setProduct(null);
    setProductSelect(null);
    setError({});
    setForm((prev) => ({
      ...prev,
      quantity: 0,
      discount: 0,
    }));
  };

  /* ================= VALIDAÇÕES ================= */

  const handleDataValidation = () => {
    let errors = {}

    if (!distributor?.value) {
      errors.distributor = "Obrigatório";
      toast({ title: "O núcleo é obrigatório.", status: "warning" });
    }

    if (!shippingType) {
      errors.shipping_type = "Obrigatório"
    }

    const balancePayment = Number(
        form?.payment_methods?.balance
            ?.replace('.','')
            ?.replace(',','.') || 0
    )

    if (!acceptZeroValue && total < 750) {
      errors.cielo_payment = "O valor mínimo para pedidos de núcleo é R$ 750,00."
    } else if (balancePayment > (total / 2)) {
      errors.balance_payment = "O saldo não pode ultrapassar metade do pedido."
    } else if (distributor?.data?.cash_balance < Math.round(balancePayment * 100)) {
      errors.balance_payment = "O núcleo não tem saldo suficiente."
    }

    if (!acceptZeroValue && Math.round(total * 100) !== Math.round((total - balancePayment) * 100) + Math.round(balancePayment * 100)) {
      errors.cielo_payment = `A soma dos pagamentos não corresponde ao total.`
    }

    setError(errors)

    return Object.keys(errors).length === 0
  }

  /* ================= SUBMIT ================= */

  const handleSubmit = async () => {
    confirmation.onClose()

    if (!handleDataValidation()) {
      toast({title:'Houve um erro de validação nos dados.', status:'warning'})
      return;
    }

    setLoading(true)

    const balancePayment = acceptZeroValue ? 0 : Number(
        form.payment_methods?.balance
            ?.replace('.','')
            ?.replace(',', '.') || 0
    );

    let paymentMethods = [
      {
        method: 9,
        amount: Math.round((total - balancePayment) * 100)
      }
    ];

    if (!acceptZeroValue && form.payment_methods?.balance) {
      if (balancePayment > 1) {
        paymentMethods.push({
          method: 3,
          amount: Math.round(balancePayment * 100)
        })
      }
    }

    const params = {
      distributor_id: distributor.value,
      shipping_type: Number(shippingType),
      payment_methods: paymentMethods,
      accept_zero_value: acceptZeroValue, // <-- NOVO PARÂMETRO
      itens: orderProducts.map((item) => {
        return {
          product_id: item.id,
          discount_percentage: item.discount_percentage,
          quantity:item.quantity
        }
      }),
    }

    try {
      const response = await API.post('/cart/direct-sale', params);
      toast({title:`Pedido de venda direta #${response?.data?.cod} cadastrada com sucesso.`, status:'info'})

      setProduct(null)
      setProductSelect(null)
      setDistributor(null)
      setOrderProducts([])
      setForm((prev) => ({
        ...prev,
        'quantity':0,
        'discount':0,
        'payment_methods': {
          'cielo': Mask.price(0),
          'balance': Mask.price(0)
        }
      }))
      setShippingType("1")
      setAcceptZeroValue(false)
      setError({})
    } catch (error) {
      toast({
        title:'Não foi possível realizar a venda direta.',
        description: error?.response?.data?.message,
        status:'warning'
      })
    } finally {
      setLoading(false)
    }
  }

  /* ================= RENDER ================= */

  return(
      <Container maxW="95%" mt={8}>
        <Heading mb={4} children="Venda Direta" />
        <hr />

        <HStack spacing={4} mt={5}>
          <Box w="50%">
            <Text mb={1}>Núcleo</Text>
            <Select2
                required
                value={distributor}
                isClearable={true}
                onChange={setDistributor}
                loadOptions={loadDistributors}
                placeholder="Pesquisar por ID, Nome, E-mail, Código ou CNPJ..."
            />
          </Box>

          <Box w="50%">
            <Text mb={1}>Produto</Text>
            <Select2
                value={productSelect}
                isClearable={true}
                onChange={handleChooseProduct}
                loadOptions={loadProducts}
                placeholder="Pesquisar pelo nome do produto..."
                isDisabled={!distributor || loading}
            />
          </Box>
        </HStack>

        <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6} mt={6}>
          <Box p={5} borderWidth="1px" borderRadius="md">
            <Text fontWeight="bold" mb={3}>
              Produto
            </Text>

            <Grid templateColumns="repeat(5, 1fr)" gap={3} alignItems="end">
              <Form.Text
                  type="number"
                  label="Quantidade"
                  name="quantity"
                  min={0}
                  value={form?.quantity}
                  onChange={handleChange}
                  ref={quantityRef}
                  isDisabled={!distributor || loading}
                  error={error?.quantity}
              />
              <Form.Text
                  type="number"
                  label="Desconto (%)"
                  min={0}
                  max={100}
                  name="discount"
                  value={form?.discount}
                  onChange={handleChange}
                  rightAddon="%"
                  isDisabled={!distributor || loading}
                  error={error?.discount}
              />
              <Form.Text
                  label="Valor Un."
                  value={Mask.price(Number(product?.consultant_price/100 ?? 0).toFixed(2))}
                  leftAddon="R$"
                  isDisabled
              />
              <Form.Text
                  label="Valor c/ Desc."
                  value={Mask.price(priceWithDiscount.toFixed(2))}
                  leftAddon="R$"
                  isDisabled
              />
              <Form.Text
                  label="Total"
                  value={Mask.price(productTotal.toFixed(2))}
                  leftAddon="R$"
                  isDisabled
              />
            </Grid>

            <Flex justify="flex-end" mt={4} gap={2}>
              <Button variant="outline" onClick={handleClear}>
                Limpar
              </Button>
              <Button colorScheme="green" onClick={handleAddProduct}>
                Incluir
              </Button>
            </Flex>
          </Box>

          {/* PAGAMENTO */}
          <Box p={5} borderWidth="1px" borderRadius="md">
            <Flex justify="space-between" align="flex-end">
              <Box flex="1">
                <Form.Radio
                    label="Tipo de Envio"
                    options={shippingTypes}
                    value={shippingType}
                    onChange={setShippingType}
                    error={error?.shipping_type}
                />
              </Box>
              <Checkbox
                  isChecked={acceptZeroValue}
                  onChange={(e) => setAcceptZeroValue(e.target.checked)}
                  mb={1}
              >
                Aceitar valor zerado
              </Checkbox>
            </Flex>

            <hr style={{ margin: "16px 0" }} />

            <Text fontSize="sm">
              Saldo atual do Núcleo: R$ {Mask.price(distributor?.data?.cash_balance ?? 0)}
            </Text>

            <HStack spacing={4} mt={3}>
              <Form.Text
                  label="Saldo"
                  name="balance_payment"
                  value={form?.payment_methods?.balance}
                  onChange={handleChange}
                  leftAddon="R$"
                  error={error?.balance_payment}
                  isDisabled={acceptZeroValue || !distributor || loading}
              />

              <Form.Text
                  label="Cielo"
                  value={form?.payment_methods?.cielo}
                  leftAddon="R$"
                  readOnly
                  error={error?.cielo_payment}
              />
            </HStack>
          </Box>
        </Grid>

        {/* TABELA */}
        <Box mt={8} borderWidth="1px" borderRadius="md" overflow="hidden">
          <Table size="sm">
            <Thead bg="gray.700">
              <Tr>
                <Th>Produto</Th>
                <Th isNumeric>Qtd</Th>
                <Th isNumeric>Desc (%)</Th>
                <Th isNumeric>Total</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {orderProducts.length === 0 && (
                  <Tr>
                    <Td colSpan={5} textAlign="center" py={6} opacity={0.7}>
                      Ainda não existem itens adicionados.
                    </Td>
                  </Tr>
              )}

              {orderProducts.map((item) => (
                  <Tr key={item.id}>
                    <Td>{item.name}</Td>
                    <Td isNumeric>{item.quantity}</Td>
                    <Td isNumeric>{item.discount_percentage}</Td>
                    <Td isNumeric>R$ {Mask.price(item.total.toFixed(2))}</Td>
                    <Td textAlign="right">
                      <IconButton
                          label="Excluir"
                          icon="trash"
                          onClick={() => handleDelete(item.id)}
                      />
                    </Td>
                  </Tr>
              ))}

              {orderProducts.length > 0 && (
                  <Tr bg="gray.900">
                    <Td colSpan={3} />
                    <Td colSpan={2} isNumeric fontWeight="bold" fontSize="lg" color="green.400">
                      Total: R$ {Mask.price(total.toFixed(2))}
                    </Td>
                  </Tr>
              )}
            </Tbody>
          </Table>
        </Box>

        <Flex justify="flex-end" mt={6}>
          <Button
              colorScheme="green"
              onClick={confirmation.onOpen}
              isDisabled={loading || !orderProducts.length}
          >
            Enviar Pedido
          </Button>
        </Flex>

        <Confirmation {...confirmation} handleSubmit={handleSubmit}/>

      </Container>
  )
}
